import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, isSameDay, isSameMonth, startOfMonth, endOfMonth, compareAsc } from "date-fns";
import { 
  Menu, Plus, Clock, MapPin, DollarSign, UserCheck, Calendar as CalendarIcon, Scissors, Pencil, Check, X, Search
} from "lucide-react";
import { PhotoThumbs } from "@/components/PhotoThumbs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import AdminSidebar from "@/components/AdminSidebar";
import AddressMatchSuggestion from "@/components/AddressMatchSuggestion";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const Schedule = () => {
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [formData, setFormData] = useState({
    customer_id: "",
    scheduled_date: "",
    scheduled_time: "morning",
    duration_minutes: 60,
    price: "",
    assigned_staff_id: "",
    notes: "",
  });
  const [editingJobId, setEditingJobId] = useState(null);
  const [editJobData, setEditJobData] = useState({ scheduled_date: "", scheduled_time: "" });
  const [customerSearch, setCustomerSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const timeSlots = [
    { value: "morning", label: "Morning (8am-12pm)" },
    { value: "afternoon", label: "Afternoon (12pm-4pm)" },
    { value: "evening", label: "Evening (4pm-7pm)" },
  ];

  const fetchData = async () => {
    try {
      const [jobsRes, customersRes, staffRes] = await Promise.all([
        axios.get(`${API}/jobs`, { withCredentials: true }),
        axios.get(`${API}/customers`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true }),
      ]);
      setJobs(jobsRes.data);
      setCustomers(customersRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load schedule data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let customerId = formData.customer_id;
      
      // If creating a new customer, create them first
      if (isNewCustomer) {
        if (!newCustomerData.name) {
          toast.error("Please fill in customer name");
          return;
        }
        
        const customerRes = await axios.post(`${API}/customers`, newCustomerData, { withCredentials: true });
        customerId = customerRes.data.customer_id;
        toast.success(`Customer "${newCustomerData.name}" created`);
      }
      
      if (!customerId) {
        toast.error("Please select or create a customer");
        return;
      }
      
      const payload = { ...formData, customer_id: customerId };
      if (!payload.assigned_staff_id) {
        delete payload.assigned_staff_id;
      }
      await axios.post(`${API}/jobs`, payload, { withCredentials: true });
      toast.success("Job scheduled successfully");
      setDialogOpen(false);
      setIsNewCustomer(false);
      setCustomerSearch("");
      setNewCustomerData({ name: "", email: "", phone: "", address: "" });
      setFormData({
        customer_id: "",
        scheduled_date: "",
        scheduled_time: "morning",
        duration_minutes: 60,
        price: "",
        assigned_staff_id: "",
        notes: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to create job:", error);
      toast.error(error.response?.data?.detail || "Failed to schedule job");
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, { status: newStatus }, { withCredentials: true });
      toast.success("Job status updated");
      fetchData();
    } catch (error) {
      console.error("Failed to update job:", error);
      toast.error("Failed to update job status");
    }
  };

  const handleTogglePayment = async (job) => {
    const newStatus = job.payment_status === "paid" ? "pending" : "paid";
    try {
      await axios.put(`${API}/jobs/${job.job_id}`, { payment_status: newStatus }, { withCredentials: true });
      toast.success(newStatus === "paid" ? "Marked as Paid" : "Marked as Payment Pending");
      fetchData();
    } catch {
      toast.error("Failed to update payment status");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { withCredentials: true });
      toast.success("Job deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to delete job:", error);
      toast.error("Failed to delete job");
    }
  };

  const handleStartEditJob = (job) => {
    setEditingJobId(job.job_id);
    setEditJobData({
      scheduled_date: job.scheduled_date?.split("T")[0] || "",
      scheduled_time: job.scheduled_time || "09:00",
    });
  };

  const handleSaveJobDateTime = async (jobId) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        scheduled_date: editJobData.scheduled_date,
        scheduled_time: editJobData.scheduled_time,
      }, { withCredentials: true });
      toast.success("Job rescheduled");
      setEditingJobId(null);
      fetchData();
    } catch (err) {
      console.error("Reschedule failed:", err?.response?.data);
      toast.error(err?.response?.data?.detail || "Failed to reschedule");
    }
  };

  const getJobsForDate = (date) => {
    return jobs.filter((job) => {
      const jobDate = parseISO(job.scheduled_date);
      return isSameDay(jobDate, date);
    });
  };

  const selectedDateJobs = getJobsForDate(selectedDate);

  const jobSearch = searchQuery.trim().toLowerCase();
  const searchResults = jobSearch
    ? jobs.filter((j) =>
        (j.customer_name || "").toLowerCase().includes(jobSearch) ||
        (j.customer_address || "").toLowerCase().includes(jobSearch) ||
        (j.notes || "").toLowerCase().includes(jobSearch) ||
        (j.assigned_staff_name || "").toLowerCase().includes(jobSearch)
      )
    : [];
  const displayedJobs = jobSearch ? searchResults : selectedDateJobs;

  const selectedCustomer = customers.find((c) => c.customer_id === formData.customer_id);
  const filteredCustomers = (() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.address || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    );
  })();

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30";
      case "in_progress": return "bg-yellow-400/20 text-yellow-400 border-yellow-400/30";
      case "cancelled": return "bg-red-400/20 text-red-400 border-red-400/30";
      default: return "bg-blue-400/20 text-blue-400 border-blue-400/30";
    }
  };

  // Get days with jobs for calendar highlighting
  const daysWithJobs = jobs.reduce((acc, job) => {
    const dateStr = job.scheduled_date.split("T")[0];
    acc[dateStr] = (acc[dateStr] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">Schedule</h1>
                <p className="text-[#A1A1AA] text-sm">{jobs.length} jobs scheduled</p>
              </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              if (!open) {
                setIsNewCustomer(false);
                setNewCustomerData({ name: "", email: "", phone: "", address: "" });
              }
              setDialogOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="schedule-job-btn"
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all font-bold rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Job
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase">Schedule New Job</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Customer Selection Toggle */}
                  <div className="flex gap-2 p-1 bg-[#0A0A0A] rounded-lg">
                    <button
                      type="button"
                      onClick={() => setIsNewCustomer(false)}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                        !isNewCustomer 
                          ? "bg-[#39FF14] text-black" 
                          : "text-[#A1A1AA] hover:text-white"
                      }`}
                    >
                      Existing Customer
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsNewCustomer(true)}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                        isNewCustomer 
                          ? "bg-[#39FF14] text-black" 
                          : "text-[#A1A1AA] hover:text-white"
                      }`}
                    >
                      + New Customer
                    </button>
                  </div>

                  {/* Existing Customer Select (searchable by name or address) */}
                  {!isNewCustomer && (
                    <div>
                      <Label className="text-[#A1A1AA]">Select Customer</Label>
                      {customers.length > 0 ? (
                        <div>
                          <Input
                            data-testid="customer-search-input"
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            placeholder="Search by name or address..."
                            className="bg-[#0A0A0A] border-[#27272A] text-white mb-2"
                          />
                          {selectedCustomer && (
                            <div className="mb-2 text-xs text-[#39FF14]" data-testid="selected-customer-label">
                              Selected: {selectedCustomer.name}
                            </div>
                          )}
                          <div className="max-h-48 overflow-y-auto border border-[#27272A] rounded-md divide-y divide-[#27272A]">
                            {filteredCustomers.length === 0 ? (
                              <div className="p-3 text-[#A1A1AA] text-sm">No matching customers</div>
                            ) : (
                              filteredCustomers.map((customer) => (
                                <button
                                  type="button"
                                  key={customer.customer_id}
                                  data-testid={`customer-option-${customer.customer_id}`}
                                  onClick={() => setFormData({ ...formData, customer_id: customer.customer_id })}
                                  className={`w-full text-left px-3 py-2 transition-colors ${
                                    formData.customer_id === customer.customer_id
                                      ? "bg-[#39FF14]/15 text-[#39FF14]"
                                      : "text-white hover:bg-[#27272A]"
                                  }`}
                                >
                                  <div className="font-medium text-sm">{customer.name}</div>
                                  {customer.address && (
                                    <div className="text-xs text-[#A1A1AA] truncate">{customer.address}</div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-[#0A0A0A] border border-[#27272A] rounded-md text-[#A1A1AA] text-sm">
                          No customers found
                        </div>
                      )}
                      {customers.length === 0 && (
                        <p className="text-yellow-500 text-xs mt-1">No customers yet. Switch to "New Customer" to create one.</p>
                      )}
                    </div>
                  )}

                  {/* New Customer Form */}
                  {isNewCustomer && (
                    <div className="space-y-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                      <p className="text-[#39FF14] text-sm font-medium">New Customer Details</p>
                      <div>
                        <Label className="text-[#A1A1AA] text-xs">Name *</Label>
                        <Input
                          value={newCustomerData.name}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                          placeholder="Customer name"
                          className="bg-[#121212] border-[#27272A] text-white h-9"
                          required={isNewCustomer}
                        />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA] text-xs">Email</Label>
                        <Input
                          type="email"
                          value={newCustomerData.email}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                          placeholder="customer@example.com"
                          className="bg-[#121212] border-[#27272A] text-white h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA] text-xs">Phone</Label>
                        <Input
                          value={newCustomerData.phone}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                          placeholder="+81 90-1234-5678"
                          className="bg-[#121212] border-[#27272A] text-white h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA] text-xs">Address</Label>
                        <Input
                          value={newCustomerData.address}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                          placeholder="Property address"
                          className="bg-[#121212] border-[#27272A] text-white h-9"
                        />
                        <AddressMatchSuggestion
                          address={newCustomerData.address}
                          onConfirm={(c) => {
                            setIsNewCustomer(false);
                            setFormData((prev) => ({ ...prev, customer_id: c.customer_id }));
                            setNewCustomerData({ name: "", email: "", phone: "", address: "" });
                            toast.success(`Linked job to ${c.name}`);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-[#A1A1AA]">Assign Staff (optional)</Label>
                    <Select 
                      value={formData.assigned_staff_id || undefined} 
                      onValueChange={(value) => setFormData({ ...formData, assigned_staff_id: value === "unassigned" ? "" : value })}
                    >
                      <SelectTrigger data-testid="staff-select" className="bg-[#0A0A0A] border-[#27272A] text-white">
                        <SelectValue placeholder="Select staff member" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        <SelectItem value="unassigned" className="text-white hover:bg-[#27272A]">
                          Unassigned
                        </SelectItem>
                        {staff.map((s) => (
                          <SelectItem 
                            key={s.staff_id} 
                            value={s.staff_id}
                            className="text-white hover:bg-[#27272A]"
                          >
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Date</Label>
                    <Input
                      data-testid="job-date-input"
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Time</Label>
                    <Select 
                      value={formData.scheduled_time} 
                      onValueChange={(value) => setFormData({ ...formData, scheduled_time: value })}
                    >
                      <SelectTrigger data-testid="time-select" className="bg-[#0A0A0A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {timeSlots.map((slot) => (
                          <SelectItem 
                            key={slot.value} 
                            value={slot.value}
                            className="text-white hover:bg-[#27272A]"
                          >
                            {slot.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Duration (minutes)</Label>
                    <Select 
                      value={String(formData.duration_minutes)} 
                      onValueChange={(value) => setFormData({ ...formData, duration_minutes: parseInt(value) })}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {[30, 45, 60, 90, 120].map((duration) => (
                          <SelectItem 
                            key={duration} 
                            value={String(duration)}
                            className="text-white hover:bg-[#27272A]"
                          >
                            {duration} minutes
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Price ($)</Label>
                    <Input
                      data-testid="job-price-input"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Notes (optional)</Label>
                    <Textarea
                      data-testid="job-notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                    />
                  </div>
                  <Button 
                    type="submit"
                    data-testid="save-job-btn"
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                  >
                    Schedule Job
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Calendar */}
              <div className="xl:col-span-1 bg-[#121212] border border-[#27272A] rounded-xl p-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  className="rounded-md"
                  modifiers={{
                    hasJobs: (date) => {
                      const dateStr = format(date, "yyyy-MM-dd");
                      return daysWithJobs[dateStr] > 0;
                    }
                  }}
                  modifiersStyles={{
                    hasJobs: {
                      backgroundColor: "rgba(57, 255, 20, 0.2)",
                      borderRadius: "50%"
                    }
                  }}
                />
                <div className="mt-4 flex items-center gap-2 text-sm text-[#A1A1AA]">
                  <div className="w-3 h-3 rounded-full bg-[#39FF14]/20"></div>
                  <span>Days with scheduled jobs</span>
                </div>
              </div>

              {/* Jobs for Selected Date / Search Results */}
              <div className="xl:col-span-2 bg-[#121212] border border-[#27272A] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <h2 className="font-heading text-xl font-bold text-white uppercase">
                    {jobSearch ? "Search Results" : format(selectedDate, "EEEE, MMMM d, yyyy")}
                  </h2>
                  <span className="text-[#A1A1AA] text-sm">{displayedJobs.length} jobs</span>
                </div>

                {/* Keyword search bar */}
                <div className="relative mb-6">
                  <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="job-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search all jobs by customer, address, staff or keyword..."
                    className="bg-[#0A0A0A] border-[#27272A] text-white pl-9 pr-9"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      data-testid="clear-job-search"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {displayedJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">{jobSearch ? "No jobs match your search" : "No jobs scheduled for this day"}</p>
                    {!jobSearch && (
                      <Button 
                        className="mt-4 bg-[#39FF14] text-black hover:bg-[#32D711]"
                        onClick={() => {
                          setFormData({ ...formData, scheduled_date: format(selectedDate, "yyyy-MM-dd") });
                          setDialogOpen(true);
                        }}
                        disabled={customers.length === 0}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Schedule Job
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {displayedJobs.map((job) => (
                      <div 
                        key={job.job_id}
                        data-testid={`schedule-job-${job.job_id}`}
                        className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 hover:border-[#39FF14]/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-white font-semibold">{job.customer_name}</h3>
                            {editingJobId === job.job_id ? (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="date"
                                  value={editJobData.scheduled_date}
                                  onChange={(e) => setEditJobData({ ...editJobData, scheduled_date: e.target.value })}
                                  className="bg-[#121212] border border-[#39FF14]/50 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-[#39FF14]"
                                  data-testid={`edit-job-date-${job.job_id}`}
                                />
                                <input
                                  type="time"
                                  value={editJobData.scheduled_time}
                                  onChange={(e) => setEditJobData({ ...editJobData, scheduled_time: e.target.value })}
                                  className="bg-[#121212] border border-[#39FF14]/50 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-[#39FF14]"
                                  data-testid={`edit-job-time-${job.job_id}`}
                                />
                                <button
                                  onClick={() => handleSaveJobDateTime(job.job_id)}
                                  className="p-1 rounded bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30 transition-colors"
                                  data-testid={`save-job-datetime-${job.job_id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingJobId(null)}
                                  className="p-1 rounded bg-red-400/20 text-red-400 hover:bg-red-400/30 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div
                                className="flex items-center gap-2 text-[#A1A1AA] text-sm mt-1 cursor-pointer hover:text-white group"
                                onClick={() => handleStartEditJob(job)}
                                data-testid={`edit-datetime-trigger-${job.job_id}`}
                              >
                                <Clock className="w-4 h-4" />
                                <span>{job.scheduled_time === "00:00" && job.status === "completed" ? "Logged" : job.scheduled_time}</span>
                                {job.duration_minutes > 0 && <><span className="text-[#27272A]">·</span><span>{job.duration_minutes} min</span></>}
                                <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 text-[#39FF14] transition-opacity" />
                              </div>
                            )}
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                            {job.status.replace("_", " ")}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-2">
                          <MapPin className="w-4 h-4" />
                          <span>{job.customer_address}</span>
                        </div>
                        {job.assigned_staff_name && (
                          <div className="flex items-center gap-2 text-[#39FF14] text-sm mb-3">
                            <UserCheck className="w-4 h-4" />
                            <span>Assigned to: {job.assigned_staff_name}</span>
                          </div>
                        )}
                        <PhotoThumbs photos={job.photos} testIdPrefix={`schedule-job-photo-${job.job_id}`} sizeClass="w-20 h-20" className="mb-3" />
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {(job.discount_applied || 0) > 0 && (
                              <span className="text-[#A1A1AA] text-sm line-through">${(job.price || 0).toFixed(2)}</span>
                            )}
                            <span className="text-[#39FF14] font-bold flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {(job.final_price || job.price || 0).toFixed(2)}
                            </span>
                            {(job.discount_applied || 0) > 0 && (
                              <span className="text-xs text-[#39FF14] bg-[#39FF14]/10 px-2 py-1 rounded">
                                -${(job.discount_applied || 0).toFixed(2)} discount
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleTogglePayment(job)}
                              className={`h-8 px-3 rounded-md text-xs font-bold border transition-all ${
                                job.payment_status === "paid"
                                  ? "bg-[#39FF14]/15 border-[#39FF14] text-[#39FF14]"
                                  : "bg-yellow-400/10 border-yellow-400/50 text-yellow-400 hover:bg-yellow-400/20"
                              }`}
                              data-testid={`payment-toggle-${job.job_id}`}
                            >
                              {job.payment_status === "paid" ? "PAID" : "UNPAID"}
                            </button>
                            <Select 
                              value={job.status}
                              onValueChange={(value) => handleStatusChange(job.job_id, value)}
                            >
                              <SelectTrigger className="w-32 bg-[#121212] border-[#27272A] text-white text-xs h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121212] border-[#27272A]">
                                <SelectItem value="scheduled" className="text-white hover:bg-[#27272A]">Scheduled</SelectItem>
                                <SelectItem value="in_progress" className="text-white hover:bg-[#27272A]">In Progress</SelectItem>
                                <SelectItem value="completed" className="text-white hover:bg-[#27272A]">Completed</SelectItem>
                                <SelectItem value="cancelled" className="text-white hover:bg-[#27272A]">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-400/30 text-red-400 hover:bg-red-400/10 h-8"
                              onClick={() => handleDeleteJob(job.job_id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Jobs List */}
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-bold text-white uppercase flex items-center gap-2">
                  <Scissors className="w-5 h-5 text-[#39FF14]" />
                  {format(currentMonth, "MMMM yyyy")} — All Jobs
                </h2>
                <span className="text-[#A1A1AA] text-sm">
                  {jobs.filter(j => {
                    try { return isSameMonth(parseISO(j.scheduled_date), currentMonth); } catch { return false; }
                  }).length} jobs this month
                </span>
              </div>

              {(() => {
                const monthJobs = jobs
                  .filter(j => {
                    try { return isSameMonth(parseISO(j.scheduled_date), currentMonth); } catch { return false; }
                  })
                  .sort((a, b) => compareAsc(parseISO(a.scheduled_date), parseISO(b.scheduled_date)));
                
                if (monthJobs.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <CalendarIcon className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
                      <p className="text-[#A1A1AA]">No jobs scheduled for {format(currentMonth, "MMMM yyyy")}</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[#A1A1AA] text-xs font-bold uppercase tracking-wider border-b border-[#27272A]">
                      <div className="col-span-2">Date</div>
                      <div className="col-span-3">Customer</div>
                      <div className="col-span-2">Time</div>
                      <div className="col-span-2">Staff</div>
                      <div className="col-span-1">Price</div>
                      <div className="col-span-2 text-right">Status</div>
                    </div>
                    {monthJobs.map(job => (
                      <div
                        key={job.job_id}
                        data-testid={`month-job-${job.job_id}`}
                        onClick={() => {
                          setSelectedDate(parseISO(job.scheduled_date));
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="grid grid-cols-2 md:grid-cols-12 gap-3 px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#27272A] hover:border-[#39FF14]/30 transition-colors cursor-pointer items-center"
                      >
                        <div className="md:col-span-2">
                          <p className="text-white text-sm font-medium">{format(parseISO(job.scheduled_date), "EEE, MMM d")}</p>
                        </div>
                        <div className="md:col-span-3">
                          <p className="text-white text-sm font-medium truncate">{job.customer_name}</p>
                          {job.customer_address && <p className="text-[#52525B] text-xs truncate">{job.customer_address}</p>}
                        </div>
                        <div className="md:col-span-2 flex items-center gap-1.5 text-[#A1A1AA] text-sm">
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                          <span>{job.scheduled_time === "00:00" && job.status === "completed" ? "Logged" : job.scheduled_time}{job.duration_minutes > 0 ? ` · ${job.duration_minutes}m` : ""}</span>
                        </div>
                        <div className="md:col-span-2 text-sm">
                          {job.assigned_staff_name ? (
                            <span className="text-[#39FF14] flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" />{job.assigned_staff_name}</span>
                          ) : (
                            <span className="text-[#52525B]">Unassigned</span>
                          )}
                        </div>
                        <div className="md:col-span-1">
                          <span className="text-[#39FF14] font-bold text-sm">${(job.final_price || job.price || 0).toFixed(2)}</span>
                        </div>
                        <div className="md:col-span-2 text-right flex items-center justify-end gap-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            job.payment_status === "paid" ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-yellow-400/10 text-yellow-400"
                          }`}>
                            {job.payment_status === "paid" ? "PAID" : "UNPAID"}
                          </span>
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                            {job.status.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Schedule;
